import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBadgesV4BadgesUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/badges/v4/badges',
        destPath: '/v4/badges',
      },
      {
        srcPath: '/_api/badges/v4/badges',
        destPath: '/v4/badges',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/badges/v4/badges',
        destPath: '/v4/badges',
      },
      {
        srcPath: '/_api/badges/v4/badges',
        destPath: '/v4/badges',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/badges/v4/badges',
        destPath: '/v4/badges',
      },
    ],
    _: [
      {
        srcPath: '/badges/v4/badges',
        destPath: '/v4/badges',
      },
      {
        srcPath: '/_api/badges/v4/badges',
        destPath: '/v4/badges',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_members_badges-v-2';

/** Creates a badge. */
export function createBadge(payload: object): RequestOptionsFactory<any> {
  function __createBadge({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'badge.createdDate' },
          { path: 'badge.updatedDate' },
          { path: 'badge.icon.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.badges.v4.badge',
      method: 'POST' as any,
      methodFqn: 'wix.badges.v4.Badges.CreateBadge',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBadgesV4BadgesUrl({
        protoPath: '/v4/badges',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badge.createdDate' },
              { path: 'badge.updatedDate' },
              { path: 'badge.icon.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createBadge;
}

/** Retrieves a badge by ID. */
export function getBadge(payload: object): RequestOptionsFactory<any> {
  function __getBadge({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v4.badge',
      method: 'GET' as any,
      methodFqn: 'wix.badges.v4.Badges.GetBadge',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBadgesV4BadgesUrl({
        protoPath: '/v4/badges/{badgeId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badge.createdDate' },
              { path: 'badge.updatedDate' },
              { path: 'badge.icon.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getBadge;
}

/**
 * Creates a query to retrieve a list of badges.
 *
 * The `queryBadges()` method builds a query to retrieve a list of badges and returns a `BadgesQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `BadgesQueryBuilder` methods onto the query. `BadgesQueryBuilder` methods enable you to sort, filter, and control the results `queryBadges()` returns.
 *
 * `queryBadges()` runs with these `BadgesQueryBuilder` defaults, which you can override:
 *
 * - limit(50)
 * - descending("_createdDate")
 *
 * The methods that are chained to `queryBadges()` are applied in the order they're called. For example, if you apply ascending('title') and then descending('id'), the results are sorted first by the title, and then, if there are multiple results with the same title, the items are sorted by ID.
 */
export function queryBadges(payload: object): RequestOptionsFactory<any> {
  function __queryBadges({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v4.badge',
      method: 'POST' as any,
      methodFqn: 'wix.badges.v4.Badges.QueryBadges',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBadgesV4BadgesUrl({
        protoPath: '/v4/badges/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badges.createdDate' },
              { path: 'badges.updatedDate' },
              { path: 'badges.icon.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryBadges;
}

/** Updates a badge. */
export function updateBadge(payload: object): RequestOptionsFactory<any> {
  function __updateBadge({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'badge.createdDate' },
          { path: 'badge.updatedDate' },
          { path: 'badge.icon.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.badges.v4.badge',
      method: 'PATCH' as any,
      methodFqn: 'wix.badges.v4.Badges.UpdateBadge',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBadgesV4BadgesUrl({
        protoPath: '/v4/badges/{badge.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badge.createdDate' },
              { path: 'badge.updatedDate' },
              { path: 'badge.icon.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateBadge;
}

/**
 * Deletes a badge.
 *
 * Deleting a badge will remove it from all members who currently have it assigned.
 */
export function deleteBadge(payload: object): RequestOptionsFactory<any> {
  function __deleteBadge({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v4.badge',
      method: 'DELETE' as any,
      methodFqn: 'wix.badges.v4.Badges.DeleteBadge',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBadgesV4BadgesUrl({
        protoPath: '/v4/badges/{badgeId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteBadge;
}

/**
 * Updates the display order of badges.
 *
 * Provide an ordered array of badge IDs to set their display sequence.
 *
 * This affects how badges appear to site members and in the site dashboard.
 * @deprecated It has been replaced with wix.badges.v4.Badges.MoveBadge(), and will be removed on 2026-07-01.
 */
export function updateBadgesDisplayOrder(
  payload: object
): RequestOptionsFactory<any> {
  function __updateBadgesDisplayOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v4.badge',
      method: 'POST' as any,
      methodFqn: 'wix.badges.v4.Badges.UpdateBadgesDisplayOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBadgesV4BadgesUrl({
        protoPath: '/v4/badges/order',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badges.createdDate' },
              { path: 'badges.updatedDate' },
              { path: 'badges.icon.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateBadgesDisplayOrder;
}

/**
 * Moves a badge to a specific position in the display order.
 *
 * You can position a badge first, last, or immediately after another badge.
 *
 * This affects how badges appear to site members and in the site dashboard.
 */
export function moveBadge(payload: object): RequestOptionsFactory<any> {
  function __moveBadge({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v4.badge',
      method: 'POST' as any,
      methodFqn: 'wix.badges.v4.Badges.MoveBadge',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBadgesV4BadgesUrl({
        protoPath: '/v4/badges/{badgeId}/move',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __moveBadge;
}
